import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import { useNavigate, useSearchParams } from 'react-router-dom';
import InlineFeedback from '../components/InlineFeedback';
import StripePaymentForm from '../components/StripePaymentForm';

const resolveApiBaseUrl = () => {
  const envBase = process.env.REACT_APP_API_BASE_URL;
  if (envBase && envBase.trim()) {
    return envBase.trim().replace(/\/+$/, '');
  }

  if (typeof window !== 'undefined' && window.location?.hostname) {
    const currentHost = window.location.hostname;
    if (currentHost === 'localhost' || currentHost === '::1' || currentHost === '[::1]') {
      return 'http://127.0.0.1:8000/api';
    }
    return `http://${currentHost}:8000/api`;
  }

  return 'http://127.0.0.1:8000/api';
};

const API_BASE = resolveApiBaseUrl();

const STEPS = [
  { id: 1, label: 'Who' },
  { id: 2, label: 'Service' },
  { id: 3, label: 'Details' },
  { id: 4, label: 'Date & Time' },
  { id: 5, label: 'Review' },
];

const BookingForm = ({ setNotification }) => {
  const token = localStorage.getItem('token');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [role, setRole] = useState('');
  const [userId, setUserId] = useState(null);
  const [currentProfile, setCurrentProfile] = useState(null);
  const [counterparts, setCounterparts] = useState([]);
  const [selectedCounterpart, setSelectedCounterpart] = useState('');
  const [services, setServices] = useState([]);
  const [selectedServices, setSelectedServices] = useState([]);

  const [attributes, setAttributes] = useState([]);
  const [attributeResponses, setAttributeResponses] = useState({});

  const [selectedDate, setSelectedDate] = useState(null);
  const [, setWorkerSchedule] = useState([]);
  const [availableDates, setAvailableDates] = useState([]);
  const [availableTimes, setAvailableTimes] = useState([]);
  const [selectedTime, setSelectedTime] = useState('');

  const [currentStep, setCurrentStep] = useState(1);
  const [stepError, setStepError] = useState('');
  const [feedback, setFeedback] = useState({ type: '', message: '' });

  const [searchRadiusMiles] = useState(50);
  const [zipInput, setZipInput] = useState('');
  const [activeZipCode, setActiveZipCode] = useState('');
  const [savingZip, setSavingZip] = useState(false);
  const [isBookingSubmitting, setIsBookingSubmitting] = useState(false);
  const [paymentState, setPaymentState] = useState(null);
  const [quoteState, setQuoteState] = useState(null);
  const [isQuoteLoading, setIsQuoteLoading] = useState(false);

  const [isProfileLoading, setIsProfileLoading] = useState(true);
  const [isDirectoryLoading, setIsDirectoryLoading] = useState(false);
  const [isScheduleLoading, setIsScheduleLoading] = useState(false);
  const [isAttributesLoading, setIsAttributesLoading] = useState(false);
  const [hasAppliedServicePrefill, setHasAppliedServicePrefill] = useState(false);

  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const selectedCounterpartProfile = useMemo(
    () => counterparts.find((item) => String(item.id) === String(selectedCounterpart)),
    [counterparts, selectedCounterpart]
  );

  const selectedService = useMemo(
    () => services.find((service) => service.id === selectedServices[0]),
    [services, selectedServices]
  );

  const getCreatorId = (service) => {
    if (typeof service?.created_by === 'object') return service?.created_by?.id;
    if (typeof service?.worker === 'object') return service?.worker?.id;
    if (service?.worker !== undefined && service?.worker !== null) return service?.worker;
    return service?.created_by;
  };

  const getCompanyId = (profile) => (
    typeof profile?.company === 'object' ? profile?.company?.id : profile?.company
  );

  const filteredServices = useMemo(() => {
    if (!selectedCounterpart) return [];

    const targetWorker = role === 'client' ? selectedCounterpartProfile : currentProfile;
    const targetWorkerId = Number(targetWorker?.id ?? userId);
    const targetCompanyId = Number(getCompanyId(targetWorker));

    return services.filter((service) => {
      if (Number(getCreatorId(service)) === targetWorkerId) return true;
      const creatorCompanyId = Number(getCompanyId(service?.created_by));
      return Boolean(
        targetCompanyId
        && creatorCompanyId
        && targetCompanyId === creatorCompanyId
      );
    });
  }, [currentProfile, selectedCounterpart, selectedCounterpartProfile, role, userId, services]);

  const maxAvailableStep = useMemo(() => {
    if (!selectedCounterpart) return 1;
    if (!selectedServices.length) return 2;
    if (!quoteState) return 3;
    if (!selectedDate || !selectedTime) return 4;
    return 5;
  }, [selectedCounterpart, selectedDate, selectedTime, selectedServices, quoteState]);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await axios.get(`${API_BASE}/userprofiles/me/`, { headers: authHeaders });
        const profile = response.data;
        const profileZip = (profile.zip_code || '').toString();
        setRole(profile.role);
        setUserId(profile.id);
        setCurrentProfile(profile);
        setZipInput(profileZip);
        setActiveZipCode(profileZip);
      } catch (error) {
        console.error(error);
        setFeedback({ type: 'error', message: 'Failed to load your profile.' });
      } finally {
        setIsProfileLoading(false);
      }
    };

    fetchProfile();
  }, [authHeaders]);

  useEffect(() => {
    if (!role) return;

    const fetchDirectoryData = async () => {
      setIsDirectoryLoading(true);
      try {
        const counterpartUrl = role === 'client'
          ? `${API_BASE}/userprofiles/workers/`
          : `${API_BASE}/userprofiles/clients/`;

        const counterpartRequestConfig = { headers: authHeaders };
        if (role === 'client') {
          const params = {
            local_only: true,
            radius_miles: searchRadiusMiles,
          };
          if (activeZipCode.trim()) params.zip_code = activeZipCode.trim();
          counterpartRequestConfig.params = params;
        }

        const [counterpartRes, servicesRes] = await Promise.all([
          axios.get(counterpartUrl, counterpartRequestConfig),
          axios.get(`${API_BASE}/services/`, { headers: authHeaders }),
        ]);

        setCounterparts(counterpartRes.data || []);
        setServices(servicesRes.data || []);

        if (role === 'client' && (counterpartRes.data || []).length === 0) {
          setFeedback({
            type: 'warning',
            message: `No workers found within ${searchRadiusMiles} miles${activeZipCode.trim() ? ` of ${activeZipCode.trim()}` : ''}.`,
          });
        }
      } catch (error) {
        console.error(error);
        setFeedback({ type: 'error', message: 'Failed to load booking directory data.' });
      } finally {
        setIsDirectoryLoading(false);
      }
    };

    fetchDirectoryData();
  }, [role, authHeaders, searchRadiusMiles, activeZipCode]);

  useEffect(() => {
    if (!selectedCounterpart) return;

    const fetchSchedule = async () => {
      setIsScheduleLoading(true);
      try {
        const workerId = role === 'client' ? selectedCounterpart : userId;
        const response = await axios.get(`${API_BASE}/schedules/?worker_id=${workerId}`, {
          headers: authHeaders,
        });

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const upcoming = (response.data || []).filter((entry) => {
          const date = new Date(entry.date);
          return date >= today;
        });

        const dates = [...new Set(upcoming.map((entry) => entry.date))];
        setWorkerSchedule(upcoming);
        setAvailableDates(dates);

        if (!dates.length) {
          setFeedback({ type: 'warning', message: 'No upcoming availability for this stylist.' });
        }
      } catch (error) {
        console.error(error);
        setWorkerSchedule([]);
        setAvailableDates([]);
        setFeedback({ type: 'error', message: 'Could not load schedule for the selected user.' });
      } finally {
        setIsScheduleLoading(false);
      }
    };

    fetchSchedule();
  }, [selectedCounterpart, role, userId, authHeaders]);

  useEffect(() => {
    if (hasAppliedServicePrefill || !services.length || !counterparts.length || !role) return;

    const serviceParam = searchParams.get('serviceId');
    const workerParam = searchParams.get('workerId');
    const serviceId = Number(serviceParam);
    const workerIdFromParam = Number(workerParam);
    if (!Number.isFinite(serviceId)) {
      setHasAppliedServicePrefill(true);
      return;
    }

    const matchedService = services.find((service) => Number(service.id) === serviceId);
    if (!matchedService) {
      setHasAppliedServicePrefill(true);
      return;
    }

    const inferredWorkerId = Number.isFinite(workerIdFromParam)
      ? workerIdFromParam
      : Number(
        typeof matchedService?.worker === 'object'
          ? matchedService?.worker?.id
          : matchedService?.worker ?? matchedService?.created_by?.id ?? matchedService?.created_by
      );

    const counterpartExists = counterparts.some(
      (profile) => Number(profile.id) === inferredWorkerId
    );

    const canAutoSelectCounterpart = role === 'client' && counterpartExists;

    if (canAutoSelectCounterpart) {
      setSelectedCounterpart(String(inferredWorkerId));
    }

    setSelectedServices([matchedService.id]);
    setAttributes([]);
    setAttributeResponses({});
    setSelectedDate(null);
    setSelectedTime('');
    setCurrentStep(canAutoSelectCounterpart ? 2 : 1);
    setStepError('');
    setFeedback({
      type: canAutoSelectCounterpart ? 'success' : 'warning',
      message: canAutoSelectCounterpart
        ? `Preselected "${matchedService.name}". Continue to date and time.`
        : `Preselected "${matchedService.name}". Select a stylist to continue.`,
    });

    setHasAppliedServicePrefill(true);
  }, [hasAppliedServicePrefill, services, counterparts, role, searchParams]);

  useEffect(() => {
    if (!selectedServices.length || !selectedCounterpart || (!attributes.length && selectedService?.attributes?.length)) {
      setQuoteState(null);
      setAvailableTimes([]);
      return;
    }
    const missingRequired = attributes.some((attribute) => {
      if (!attribute.required) return false;
      const value = attributeResponses[attribute.id];
      return Array.isArray(value) ? !value.length : value === undefined || value === null || value === '' || value === false;
    });
    if (missingRequired) {
      setQuoteState(null);
      setAvailableTimes([]);
      return;
    }

    const serviceId = selectedServices[0];
    const grouped = { [serviceId]: {} };
    attributes.forEach((attribute) => {
      if (Object.prototype.hasOwnProperty.call(attributeResponses, attribute.id)) {
        grouped[serviceId][attribute.id] = attributeResponses[attribute.id];
      }
    });
    const workerId = role === 'client' ? selectedCounterpart : userId;
    const requestQuote = async () => {
      setIsQuoteLoading(true);
      try {
        const response = await axios.post(`${API_BASE}/bookings/quote/`, {
          worker_id: workerId,
          services: selectedServices,
          attribute_responses: grouped,
          ...(selectedDate ? { date: selectedDate.toISOString().split('T')[0] } : {}),
        }, { headers: authHeaders });
        setQuoteState(response.data);
        const times = response.data?.available_start_times || [];
        setAvailableTimes(times);
        if (selectedTime && !times.includes(selectedTime)) setSelectedTime('');
      } catch (error) {
        setQuoteState(null);
        setAvailableTimes([]);
      } finally {
        setIsQuoteLoading(false);
      }
    };
    requestQuote();
  }, [selectedServices, selectedCounterpart, attributes, attributeResponses, selectedDate, role, userId, authHeaders, selectedTime, selectedService]);

  useEffect(() => {
    const fetchAttributes = async () => {
      if (!selectedServices.length) {
        setAttributes([]);
        setAttributeResponses({});
        return;
      }

      setIsAttributesLoading(true);
      try {
        const responses = await Promise.all(
          selectedServices.map((serviceId) =>
            axios.get(`${API_BASE}/attributes/?service=${serviceId}`, { headers: authHeaders })
          )
        );

        const merged = responses.flatMap((result) => result.data || []);
        setAttributes(merged);
        setAttributeResponses((previous) => {
          const next = {};
          merged.forEach((attribute) => {
            if (Object.prototype.hasOwnProperty.call(previous, attribute.id)) {
              next[attribute.id] = previous[attribute.id];
              return;
            }

            if (attribute.input_type === 'range') {
              next[attribute.id] = String(attribute.settings?.min ?? 0);
            }
          });
          return next;
        });
      } catch (error) {
        console.error(error);
        setAttributes([]);
        setFeedback({ type: 'error', message: 'Failed to load service questions.' });
      } finally {
        setIsAttributesLoading(false);
      }
    };

    fetchAttributes();
  }, [selectedServices, authHeaders]);

  const handleApplyZipFilter = () => {
    const trimmed = zipInput.trim();
    setSelectedCounterpart('');
    setSelectedDate(null);
    setSelectedTime('');
    setSelectedServices([]);
    setAttributes([]);
    setAttributeResponses({});
    setWorkerSchedule([]);
    setAvailableDates([]);
    setAvailableTimes([]);
    setActiveZipCode(trimmed);
    setCurrentStep(1);
  };

  const handleSaveZipToProfile = async () => {
    if (role !== 'client') return;

    setSavingZip(true);
    try {
      await axios.patch(
        `${API_BASE}/userprofiles/me/`,
        { zip_code: zipInput.trim() },
        { headers: authHeaders }
      );
      setActiveZipCode(zipInput.trim());
      setFeedback({ type: 'success', message: 'Zip code saved to your profile.' });
      setNotification?.({ type: 'success', message: 'Zip code saved to your profile.' });
    } catch (error) {
      console.error(error);
      const apiMessage = error?.response?.data?.error || error?.response?.data?.detail;
      setFeedback({ type: 'error', message: apiMessage || 'Failed to save zip code.' });
    } finally {
      setSavingZip(false);
    }
  };

  const handleCounterpartChange = (value) => {
    setSelectedCounterpart(value);
    setSelectedDate(null);
    setSelectedTime('');
    setSelectedServices([]);
    setAttributes([]);
    setAttributeResponses({});
    setCurrentStep(1);
    setStepError('');
  };

  const handleDateChange = (value) => {
    const resolved = Array.isArray(value) ? value[0] : value;
    setSelectedDate(resolved);
    setSelectedTime('');
    setStepError('');
  };

  const handleAttributeChange = (attributeId, value) => {
    setQuoteState(null);
    setAvailableTimes([]);
    setSelectedTime('');
    setStepError('');
    setAttributeResponses((prev) => ({ ...prev, [attributeId]: value }));
  };

  const hasAttributeResponse = (attribute) => {
    if (!Object.prototype.hasOwnProperty.call(attributeResponses, attribute.id)) return false;
    const value = attributeResponses[attribute.id];
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === 'string') return value.trim().length > 0;
    return value !== null && value !== undefined;
  };

  const canContinueFromStep = (step) => {
    if (step === 1) return !!selectedCounterpart;
    if (step === 2) return selectedServices.length > 0;
    if (step === 3) return attributes.every((attribute) => !attribute.required || hasAttributeResponse(attribute)) && !!quoteState;
    if (step === 4) return !!selectedDate && !!selectedTime;
    return true;
  };

  const handleNextStep = () => {
    if (!canContinueFromStep(currentStep)) {
      if (currentStep === 1) setStepError('Please choose who the booking is for first.');
      if (currentStep === 2) setStepError('Please select a service to continue.');
      if (currentStep === 3) setStepError('Please answer all required service questions and wait for the price quote.');
      if (currentStep === 4) setStepError('Please choose both a date and a time slot.');
      return;
    }

    setStepError('');
    setCurrentStep((prev) => Math.min(5, prev + 1));
  };

  const handlePreviousStep = () => {
    setStepError('');
    setCurrentStep((prev) => Math.max(1, prev - 1));
  };

  const formatAttributeValue = (attribute, value) => {
    const labelFor = (candidate) => {
      const option = (attribute.options || []).find((item) => String(item.id) === String(candidate) || item.value === candidate);
      return option?.value ?? candidate;
    };
    if (Array.isArray(value)) return value.map(labelFor).join(', ');
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    return value !== undefined && value !== null && value !== '' ? labelFor(value) : '—';
  };

  const handleSubmit = async () => {
    if (!selectedDate || !selectedTime || !selectedServices.length || !selectedCounterpart) {
      setFeedback({ type: 'error', message: 'Please complete all required booking steps before confirming.' });
      return;
    }

    try {
      setIsBookingSubmitting(true);
      setPaymentState(null);

      const groupedResponses = {};
      attributes.forEach((attribute) => {
        const serviceId = typeof attribute.service === 'object' ? attribute.service?.id : attribute.service;
        if (!groupedResponses[serviceId]) groupedResponses[serviceId] = {};
        groupedResponses[serviceId][attribute.id] = attributeResponses[attribute.id];
      });

      const bookingPayload = {
        date: selectedDate.toISOString().split('T')[0],
        time: selectedTime,
        services: selectedServices,
        attribute_responses: groupedResponses,
        client_id: role === 'client' ? userId : selectedCounterpart,
        worker_id: role === 'client' ? selectedCounterpart : userId,
      };

      const bookingResponse = await axios.post(`${API_BASE}/bookings/`, bookingPayload, {
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json',
        },
      });

      const createdBooking = bookingResponse.data;
      if (createdBooking?.payment_action_required && role === 'client') {
        try {
          const paymentResponse = await axios.post(
            `${API_BASE}/bookings/${createdBooking.id}/initialize_payment/`,
            {},
            { headers: authHeaders }
          );
          setPaymentState(paymentResponse.data || null);
          setNotification?.({ type: 'success', message: 'Booking created. Complete the secure test deposit below.' });
        } catch (paymentError) {
          console.error(paymentError?.response?.data || paymentError.message);
          const message = `Booking #${createdBooking.id} was created, but secure deposit checkout could not start. Open it in My Bookings to resume payment.`;
          setNotification?.({ type: 'error', message });
          navigate('/myBookings?payment_initialization_failed=1');
        }
        return;
      }

      if (createdBooking?.payment_action_required) {
        setNotification?.({ type: 'success', message: 'Booking created. The client must complete the deposit before confirmation.' });
      } else {
        setNotification?.({ type: 'success', message: 'Booking created successfully.' });
      }

      navigate('/myBookings');
    } catch (error) {
      console.error(error?.response?.data || error.message);
      const apiError = error?.response?.data?.error || 'Booking failed.';
      setFeedback({ type: 'error', message: apiError });
      setNotification?.({ type: 'error', message: apiError });
    } finally {
      setIsBookingSubmitting(false);
    }
  };

  const handleStripePaymentSubmitted = ({ status }) => {
    setNotification?.({
      type: 'success',
      message: `Deposit submitted to Stripe (${status}). Booking status will update after webhook verification.`,
    });
    navigate('/myBookings?payment_submitted=1');
  };

  const tileDisabled = ({ date }) => {
    const dateString = date.toISOString().split('T')[0];
    return !availableDates.includes(dateString);
  };

  return (
    <div className="min-h-screen bg-[#F6F4F8] px-3 py-4 text-[#2E2E2E] sm:px-4 sm:py-8">
      <div className="mx-auto max-w-6xl space-y-6">
        <header className="rounded-2xl bg-gradient-to-r from-[#6D3B7E] to-[#8A4AA3] p-4 text-white shadow-lg sm:p-6">
          <h1 className="text-2xl font-bold sm:text-3xl">Book an Appointment</h1>
          <p className="mt-2 text-sm text-purple-100 md:text-base">
            A clear step-by-step booking flow for faster scheduling and fewer mistakes.
          </p>
        </header>

        <InlineFeedback type={feedback.type || 'info'} message={feedback.message} />
        <InlineFeedback type={stepError ? 'warning' : 'info'} message={stepError} />

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_320px]">
          <section className="rounded-2xl bg-white p-4 shadow-sm sm:p-5">
            <ol className="mb-6 grid grid-cols-2 gap-2 sm:grid-cols-5" aria-label="Booking steps">
              {STEPS.map((step) => {
                const isActive = currentStep === step.id;
                const isDone = currentStep > step.id;
                const isLocked = step.id > maxAvailableStep;

                return (
                  <li key={step.id}>
                    <button
                      type="button"
                      disabled={isLocked}
                      onClick={() => setCurrentStep(step.id)}
                      className={`w-full rounded-xl border px-3 py-2 text-xs font-semibold transition sm:text-sm ${
                        isActive
                          ? 'border-[#6D3B7E] bg-[#F6F0FA] text-[#4B2A56]'
                          : isDone
                            ? 'border-green-200 bg-green-50 text-green-800'
                            : 'border-gray-200 bg-white text-gray-600'
                      } ${isLocked ? 'cursor-not-allowed opacity-50' : ''}`}
                    >
                      {isDone ? '✓ ' : ''}
                      {step.label}
                    </button>
                  </li>
                );
              })}
            </ol>

            {isProfileLoading ? (
              <InlineFeedback type="info" message="Loading your booking profile..." />
            ) : null}

            {currentStep === 1 && (
              <div className="space-y-5">
                {role === 'client' && (
                  <section className="rounded-xl border border-[#E5D4ED] bg-[#FBF7FD] p-4">
                    <h2 className="text-base font-semibold text-[#4B2A56]">Find nearby stylists</h2>
                    <p className="mt-1 text-sm text-gray-600">
                      Search within {searchRadiusMiles} miles of your preferred zip code.
                    </p>
                    <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                      <label htmlFor="zipInput" className="sr-only">Zip code</label>
                      <input
                        id="zipInput"
                        type="text"
                        value={zipInput}
                        onChange={(event) => setZipInput(event.target.value)}
                        placeholder="Enter zip code"
                        className="flex-1 rounded-lg border border-gray-300 px-3 py-2"
                        maxLength={10}
                      />
                      <button
                        type="button"
                        onClick={handleApplyZipFilter}
                        className="rounded-lg bg-[#6D3B7E] px-4 py-2 text-sm font-semibold text-white hover:bg-[#5A2F67]"
                      >
                        Apply
                      </button>
                      <button
                        type="button"
                        onClick={handleSaveZipToProfile}
                        disabled={savingZip}
                        className="rounded-lg bg-[#CDA349] px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
                      >
                        {savingZip ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  </section>
                )}

                <div>
                  <label className="mb-1 block text-sm font-semibold text-[#4B2A56]" htmlFor="counterpartSelect">
                    {role === 'client' ? 'Choose a stylist' : 'Choose a client'}
                  </label>
                  <select
                    id="counterpartSelect"
                    value={selectedCounterpart}
                    onChange={(event) => handleCounterpartChange(event.target.value)}
                    disabled={isDirectoryLoading || isProfileLoading}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2"
                  >
                    <option value="">Select...</option>
                    {counterparts.map((profile) => (
                      <option key={profile.id} value={profile.id}>
                        {profile.first_name} {profile.last_name}
                        {profile.role === 'owner' ? ' (Owner)' : ''}
                        {typeof profile.distance_miles === 'number' ? ` (${profile.distance_miles} mi)` : ''}
                      </option>
                    ))}
                  </select>
                  {isDirectoryLoading ? <p className="mt-2 text-sm text-gray-500">Loading directory...</p> : null}
                </div>
              </div>
            )}

            {currentStep === 4 && (
              <div className="space-y-5">
                <div>
                  <h2 className="text-base font-semibold text-[#4B2A56]">Choose date</h2>
                  <p className="mt-1 text-sm text-gray-600">Only available dates are selectable.</p>
                </div>
                <div className="rounded-xl border border-gray-200 p-3">
                  <Calendar
                    value={selectedDate}
                    onChange={handleDateChange}
                    tileDisabled={tileDisabled}
                    tileClassName={({ date }) =>
                      selectedDate && date.toDateString() === selectedDate.toDateString()
                        ? 'bg-[#CDA349] text-white rounded-full'
                        : ''
                    }
                  />
                    {isScheduleLoading || isQuoteLoading ? <p className="mt-3 text-sm text-gray-500">Finding times that fit the full appointment...</p> : null}
                </div>

                {selectedDate && (
                  <div>
                    <h3 className="text-sm font-semibold text-[#4B2A56]">Select time</h3>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {availableTimes.map((time) => (
                        <button
                          key={time}
                          type="button"
                          onClick={() => {
                            setSelectedTime(time);
                            setStepError('');
                          }}
                          className={`rounded-full px-4 py-2 text-sm transition ${
                            selectedTime === time
                              ? 'bg-[#6D3B7E] text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                          aria-pressed={selectedTime === time}
                        >
                          {time}
                        </button>
                      ))}
                    </div>
                    {!availableTimes.length ? <p className="mt-2 text-sm text-gray-500">No time slots available for this date.</p> : null}
                  </div>
                )}
              </div>
            )}

            {currentStep === 2 && (
              <div className="space-y-4">
                <div>
                  <h2 className="text-base font-semibold text-[#4B2A56]">Choose service</h2>
                  <p className="mt-1 text-sm text-gray-600">Pick one service for this booking.</p>
                </div>

                {!filteredServices.length ? (
                  <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 px-4 py-5 text-sm text-gray-600">
                    No services available for this selection.
                  </div>
                ) : (
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {filteredServices.map((service) => {
                      const isSelected = selectedServices.includes(service.id);
                      return (
                        <button
                          key={service.id}
                          type="button"
                          onClick={() => {
                            setSelectedServices([service.id]);
                            setStepError('');
                          }}
                          className={`overflow-hidden rounded-xl border text-left transition ${
                            isSelected
                              ? 'border-[#6D3B7E] bg-[#F6F0FA] text-[#4B2A56]'
                              : 'border-gray-200 bg-white hover:bg-gray-50'
                          }`}
                          aria-pressed={isSelected}
                        >
                          <div className="aspect-[16/8] bg-gradient-to-br from-[#EEDFF4] to-[#D7BDE2]">
                            {service.image_data ? (
                              <img src={service.image_data} alt="" className="h-full w-full object-cover" />
                            ) : (
                              <div className="flex h-full items-center justify-center text-3xl text-[#6D3B7E]" aria-hidden="true">✦</div>
                            )}
                          </div>
                          <div className="p-4">
                            <h3 className="font-semibold">{service.name}</h3>
                            <p className="mt-1 line-clamp-2 text-sm text-gray-600">{service.description || 'No description available.'}</p>
                            <p className="mt-2 text-xs font-semibold text-[#6D3B7E]">
                              ${service.price} • {service.duration} min
                            </p>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {currentStep === 3 && (
              <div className="space-y-4">
                <div>
                  <h2 className="text-base font-semibold text-[#4B2A56]">Service details</h2>
                  <p className="mt-1 text-sm text-gray-600">Answer optional or required service questions.</p>
                </div>

                {isAttributesLoading ? (
                  <p className="text-sm text-gray-500">Loading service questions...</p>
                ) : attributes.length === 0 ? (
                  <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 px-4 py-5 text-sm text-gray-600">
                    No additional questions for this service.
                  </div>
                ) : (
                  <div className="space-y-4">
                    {attributes.map((attribute) => {
                      const inputType = attribute.input_type;
                      const optionRows = (attribute.options || []).map((option) =>
                        typeof option === 'object' ? option : { value: option, extra_price: '0.00', extra_duration_minutes: 0 }
                      );
                      const optionAdjustment = (option) => {
                        const parts = [];
                        if (Number(option.extra_price || 0) > 0) parts.push(`+$${Number(option.extra_price).toFixed(2)}`);
                        if (Number(option.extra_duration_minutes || 0) > 0) parts.push(`+${option.extra_duration_minutes} min`);
                        return parts.length ? parts.join(' • ') : 'Included';
                      };

                      const value = attributeResponses[attribute.id];
                      const settings = attribute.settings || {};
                      const isMissingRequired = attribute.required && !hasAttributeResponse(attribute);

                      const toggleMultiSelect = (optionValue) => {
                        const current = Array.isArray(value) ? value : [];
                        const exists = current.includes(optionValue);
                        const next = exists
                          ? current.filter((item) => item !== optionValue)
                          : [...current, optionValue];
                        handleAttributeChange(attribute.id, next);
                      };

                      return (
                        <div
                          key={attribute.id}
                          className={`rounded-xl border p-4 ${isMissingRequired ? 'border-amber-300 bg-amber-50/40' : 'border-gray-200'}`}
                        >
                          <div className="mb-3">
                            <label className="block text-sm font-semibold text-[#4B2A56]">
                              {attribute.name}
                              {attribute.required ? <span className="ml-1 text-red-600" aria-label="required">*</span> : null}
                            </label>
                            {attribute.help_text ? <p className="mt-1 text-xs text-gray-500">{attribute.help_text}</p> : null}
                          </div>

                          {(inputType === 'dropdown' || inputType === 'select') && (
                            <select
                              value={value || ''}
                              onChange={(event) => handleAttributeChange(attribute.id, event.target.value)}
                              className="min-h-11 w-full rounded-xl border border-gray-300 bg-white px-3 py-2"
                            >
                              <option value="">Select...</option>
                              {optionRows.map((option) => (
                                <option key={`${attribute.id}-${option.id ?? option.value}`} value={option.id ?? option.value}>
                                  {option.value} — {optionAdjustment(option)}
                                </option>
                              ))}
                            </select>
                          )}

                          {inputType === 'radio' && (
                            <div className="grid gap-2 sm:grid-cols-2">
                              {optionRows.map((option) => {
                                const optionValue = String(option.id ?? option.value);
                                return (
                                <label
                                  key={`${attribute.id}-${optionValue}`}
                                  className={`flex min-h-11 cursor-pointer items-center gap-2 rounded-xl border px-3 py-2 text-sm transition ${
                                    value === optionValue
                                      ? 'border-[#6D3B7E] bg-[#F4EBF8] font-semibold text-[#4B2A56]'
                                      : 'border-gray-200 bg-white hover:bg-gray-50'
                                  }`}
                                >
                                  <input
                                    type="radio"
                                    name={`attribute-${attribute.id}`}
                                    value={optionValue}
                                    checked={value === optionValue}
                                    onChange={(event) => handleAttributeChange(attribute.id, event.target.value)}
                                  />
                                  <span>{option.value}<span className="block text-xs font-normal text-gray-500">{optionAdjustment(option)}</span></span>
                                </label>
                                );
                              })}
                            </div>
                          )}

                          {inputType === 'multiselect' && (
                            <div className="grid gap-2 sm:grid-cols-2">
                              {optionRows.map((option) => {
                                const optionValue = String(option.id ?? option.value);
                                return (
                                <label
                                  key={`${attribute.id}-${optionValue}`}
                                  className={`flex min-h-11 cursor-pointer items-center gap-2 rounded-xl border px-3 py-2 text-sm transition ${
                                    Array.isArray(value) && value.includes(optionValue)
                                      ? 'border-[#6D3B7E] bg-[#F4EBF8] font-semibold text-[#4B2A56]'
                                      : 'border-gray-200 bg-white hover:bg-gray-50'
                                  }`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={Array.isArray(value) ? value.includes(optionValue) : false}
                                    onChange={() => toggleMultiSelect(optionValue)}
                                  />
                                  <span>{option.value}<span className="block text-xs font-normal text-gray-500">{optionAdjustment(option)}</span></span>
                                </label>
                                );
                              })}
                            </div>
                          )}

                          {inputType === 'color' && (
                            <div className="flex flex-wrap gap-3">
                              {(attribute.options || []).map((option, index) => {
                                const optionLabel = typeof option === 'object' ? option.value : option;
                                const optionValue = String(typeof option === 'object' ? option.id ?? option.value : option);
                                const swatchColor = typeof option === 'object' && option.color ? option.color : optionLabel;
                                const isSelected = String(value) === optionValue;
                                return (
                                  <button
                                    key={`${attribute.id}-${optionValue}-${index}`}
                                    type="button"
                                    onClick={() => handleAttributeChange(attribute.id, optionValue)}
                                    aria-pressed={isSelected}
                                    className={`min-w-[88px] rounded-xl border p-2 text-center text-xs font-medium transition ${
                                      isSelected
                                        ? 'border-[#6D3B7E] bg-[#F4EBF8] ring-2 ring-[#D8C0E1]'
                                        : 'border-gray-200 bg-white hover:border-gray-300'
                                    }`}
                                  >
                                    <span
                                      className="mx-auto block h-10 w-10 rounded-full border-2 border-white shadow ring-1 ring-gray-200"
                                      style={{ backgroundColor: swatchColor }}
                                    />
                                    <span className="mt-2 block text-gray-700">{optionLabel}</span>
                                    <span className="mt-1 block text-[11px] text-gray-500">{optionAdjustment(option)}</span>
                                  </button>
                                );
                              })}
                            </div>
                          )}

                          {(inputType === 'checkbox' || inputType === 'boolean' || inputType === 'toggle') && (
                            <label className="inline-flex min-h-11 cursor-pointer items-center gap-3 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm">
                              <input
                                type="checkbox"
                                checked={Boolean(value)}
                                onChange={(event) => handleAttributeChange(attribute.id, event.target.checked)}
                                className="peer sr-only"
                              />
                              <span className="relative h-6 w-11 rounded-full bg-gray-300 transition peer-checked:bg-[#6D3B7E] after:absolute after:left-1 after:top-1 after:h-4 after:w-4 after:rounded-full after:bg-white after:transition-transform peer-checked:after:translate-x-5" />
                              <span className="font-medium text-gray-700">
                                {Boolean(value) ? 'Yes' : 'No'}
                                {optionRows[0] ? <span className="ml-2 text-xs font-normal text-gray-500">{optionAdjustment(optionRows[0])}</span> : null}
                              </span>
                            </label>
                          )}

                          {inputType === 'range' && (
                            <div>
                              <div className="mb-2 flex items-center justify-between text-sm">
                                <span className="text-gray-500">{settings.min ?? 0}{settings.unit ? ` ${settings.unit}` : ''}</span>
                                <output className="rounded-full bg-[#F4EBF8] px-3 py-1 font-semibold text-[#6D3B7E]">
                                  {value ?? settings.min ?? 0}{settings.unit ? ` ${settings.unit}` : ''}
                                </output>
                                <span className="text-gray-500">{settings.max ?? 10}{settings.unit ? ` ${settings.unit}` : ''}</span>
                              </div>
                              <input
                                type="range"
                                min={settings.min ?? 0}
                                max={settings.max ?? 10}
                                step={settings.step ?? 1}
                                value={value ?? settings.min ?? 0}
                                onChange={(event) => handleAttributeChange(attribute.id, event.target.value)}
                                className="h-2 w-full cursor-pointer accent-[#6D3B7E]"
                              />
                              {(() => {
                                const selectedOption = optionRows.find((option) => String(option.value) === String(value ?? settings.min ?? 0));
                                return selectedOption ? <p className="mt-2 text-center text-xs font-medium text-[#6D3B7E]">{optionAdjustment(selectedOption)}</p> : null;
                              })()}
                            </div>
                          )}

                          {inputType === 'textarea' && (
                            <textarea
                              value={value || ''}
                              onChange={(event) => handleAttributeChange(attribute.id, event.target.value)}
                              placeholder={attribute.placeholder || 'Tell us what you have in mind'}
                              className="min-h-[112px] w-full rounded-xl border border-gray-300 px-3 py-2"
                            />
                          )}

                          {(['text', 'number'].includes(inputType) || !inputType) && (
                            <input
                              type={inputType || 'text'}
                              value={value || ''}
                              onChange={(event) => handleAttributeChange(attribute.id, event.target.value)}
                              placeholder={attribute.placeholder || ''}
                              min={inputType === 'number' ? settings.min : undefined}
                              max={inputType === 'number' ? settings.max : undefined}
                              step={inputType === 'number' ? settings.step : undefined}
                              className="min-h-11 w-full rounded-xl border border-gray-300 px-3 py-2"
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {currentStep === 5 && (
              <div className="space-y-4">
                <h2 className="text-base font-semibold text-[#4B2A56]">Review your booking</h2>
                <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm">
                  <p><span className="font-semibold">With:</span> {selectedCounterpartProfile ? `${selectedCounterpartProfile.first_name} ${selectedCounterpartProfile.last_name}` : '—'}</p>
                  <p><span className="font-semibold">Date:</span> {selectedDate ? selectedDate.toDateString() : '—'}</p>
                  <p><span className="font-semibold">Time:</span> {selectedTime || '—'}</p>
                  <p><span className="font-semibold">Service:</span> {selectedService?.name || '—'}</p>
                </div>

                {attributes.length > 0 && (
                  <div className="rounded-xl border border-gray-200 p-4">
                    <h3 className="text-sm font-semibold text-[#4B2A56]">Service details</h3>
                    <ul className="mt-2 space-y-1 text-sm text-gray-700">
                      {attributes.map((attribute) => (
                        <li key={attribute.id}>
                          <span className="font-medium">{attribute.name}:</span> {formatAttributeValue(attribute, attributeResponses[attribute.id])}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {quoteState ? (
                  <div className="rounded-xl border border-[#E5D4ED] bg-[#FBF7FD] p-4 text-sm">
                    <h3 className="font-semibold text-[#4B2A56]">Price and appointment time</h3>
                    <div className="mt-3 space-y-1">
                      <p className="flex justify-between"><span>Base service</span><strong>${(Number(quoteState.pricing_summary?.base_amount_cents || 0) / 100).toFixed(2)}</strong></p>
                      <p className="flex justify-between"><span>Selected options</span><strong>+${(Number(quoteState.pricing_summary?.attribute_amount_cents || 0) / 100).toFixed(2)}</strong></p>
                      <p className="flex justify-between border-t border-purple-100 pt-2 text-base text-[#4B2A56]"><span>Total</span><strong>${(Number(quoteState.pricing_summary?.total_amount_cents || 0) / 100).toFixed(2)}</strong></p>
                      <p className="pt-1 text-gray-600">Appointment length: <strong>{quoteState.duration_summary?.total_minutes || 0} minutes</strong>{Number(quoteState.duration_summary?.attribute_minutes || 0) > 0 ? ` (${quoteState.duration_summary.attribute_minutes} extra)` : ''}</p>
                    </div>
                  </div>
                ) : null}

                {paymentState?.payment?.status ? (
                  <div className="space-y-4 rounded-xl border border-[#E5D4ED] bg-[#FBF7FD] p-4 text-sm">
                    <p className="font-semibold text-[#4B2A56]">Deposit payment</p>
                    <p>Status: {paymentState.payment.status}</p>
                    <p>
                      Amount: ${(Number(paymentState.payment.amount_cents || 0) / 100).toFixed(2)}{' '}
                      {(paymentState.payment.currency || 'usd').toUpperCase()}
                    </p>
                    <StripePaymentForm
                      publishableKey={paymentState.publishable_key}
                      clientSecret={paymentState.payment.client_secret}
                      bookingId={paymentState.booking_id}
                      amountCents={paymentState.payment.amount_cents}
                      currency={paymentState.payment.currency}
                      onPaymentSubmitted={handleStripePaymentSubmitted}
                    />
                  </div>
                ) : null}
              </div>
            )}

            <div className="mt-8 grid grid-cols-2 items-center gap-2 border-t border-gray-200 pt-4 sm:flex sm:flex-wrap sm:justify-between">
              <button
                type="button"
                onClick={handlePreviousStep}
                disabled={currentStep === 1}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Back
              </button>

              {currentStep < 5 ? (
                <button
                  type="button"
                  onClick={handleNextStep}
                  className="rounded-lg bg-[#6D3B7E] px-4 py-2 text-sm font-semibold text-white hover:bg-[#5A2F67]"
                >
                  Continue
                </button>
              ) : paymentState?.payment?.client_secret ? (
                <button
                  type="button"
                  onClick={() => navigate('/myBookings')}
                  className="col-span-2 rounded-lg border border-[#6D3B7E] px-4 py-2 text-sm font-semibold text-[#6D3B7E] hover:bg-[#FBF7FD] sm:col-auto"
                >
                  Finish payment later in My Bookings
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={isBookingSubmitting}
                  className="rounded-lg bg-[#10B981] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0F9A6D] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isBookingSubmitting ? 'Processing...' : 'Confirm Booking'}
                </button>
              )}
            </div>
          </section>

          <aside className="h-fit rounded-2xl bg-white p-4 shadow-sm lg:sticky lg:top-6">
            <h2 className="text-base font-semibold text-[#4B2A56]">Booking summary</h2>
            <ul className="mt-3 space-y-2 text-sm text-gray-700">
              <li className="rounded-lg bg-gray-50 px-3 py-2">
                <span className="block text-xs uppercase text-gray-500">Step 1</span>
                {selectedCounterpartProfile
                  ? `${selectedCounterpartProfile.first_name} ${selectedCounterpartProfile.last_name}`
                  : 'Choose person'}
              </li>
              <li className="rounded-lg bg-gray-50 px-3 py-2">
                <span className="block text-xs uppercase text-gray-500">Step 2</span>
                {selectedService?.name || 'Choose service'}
              </li>
              <li className="rounded-lg bg-gray-50 px-3 py-2">
                <span className="block text-xs uppercase text-gray-500">Step 3</span>
                {quoteState
                  ? `$${(Number(quoteState.pricing_summary?.total_amount_cents || 0) / 100).toFixed(2)} • ${quoteState.duration_summary?.total_minutes || 0} min`
                  : attributes.length ? `${attributes.length} detail field(s)` : 'No extra details required'}
              </li>
              <li className="rounded-lg bg-gray-50 px-3 py-2">
                <span className="block text-xs uppercase text-gray-500">Step 4</span>
                {selectedDate ? selectedDate.toDateString() : 'Choose date'}
                <br />
                {selectedTime || 'Choose time'}
              </li>
            </ul>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default BookingForm;
